import { supabase } from "./client";
import { isSupabaseConfigured, missingSupabaseConfig } from "./config";
import {
  normalizeCuit,
  normalizeFiscalEnvironment,
  normalizeFiscalContact,
  validateFiscalContact,
  validateRestaurantFiscalDraft,
} from "../local/fiscalConnection";

const ensureSupabaseReady = () => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error(
      `Faltan variables de entorno para Supabase: ${missingSupabaseConfig.join(", ")}`
    );
  }
};

const getErrorText = (error) =>
  `${error?.message || ""} ${error?.details || ""} ${error?.hint || ""}`.toLowerCase();

const isMissingFiscalConnectionSchema = (error) => {
  const message = getErrorText(error);
  const missingRelation =
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    message.includes("relation \"public.fiscal_issuers\" does not exist") ||
    message.includes("relation \"public.fiscal_points_of_sale\" does not exist");
  const missingFunction =
    error?.code === "42883" ||
    error?.code === "PGRST202" ||
    message.includes("could not find the function");

  return (
    missingRelation ||
    (missingFunction &&
      (message.includes("save_restaurant_fiscal_issuer_draft") ||
        message.includes("save_restaurant_fiscal_point_of_sale") ||
        message.includes("request_restaurant_fiscal_connection_verification") ||
        message.includes("save_restaurant_fiscal_onboarding_draft")))
  );
};

const normalizeFiscalConnectionError = (error, fallbackMessage) => {
  if (isMissingFiscalConnectionSchema(error) && getErrorText(error).includes("save_restaurant_fiscal_onboarding_draft")) {
    return new Error("La actualizacion de datos fiscales aun no esta habilitada en este entorno. No se guardaron cambios; conserva el formulario y avisa a soporte.");
  }
  if (isMissingFiscalConnectionSchema(error)) {
    return new Error(
      "Falta aplicar restaurant_fiscal_connection_phase2_patch.sql en Supabase para usar la vinculacion ARCA."
    );
  }

  return new Error(error?.message || fallbackMessage);
};

export const normalizeIssuer = (row = {}) => ({
  id: row.id || "",
  companyId: row.company_id || row.companyId || "",
  environment: normalizeFiscalEnvironment(row.environment),
  cuit: normalizeCuit(row.cuit),
  legalName: row.legal_name || row.legalName || "",
  ivaConditionId: row.iva_condition_id ?? row.ivaConditionId ?? null,
  ivaConditionLabel: row.iva_condition_label || row.ivaConditionLabel || "",
  grossIncomeNumber: row.gross_income_number || row.grossIncomeNumber || "",
  fiscalAddress: row.fiscal_address || row.fiscalAddress || "",
  delegationStatus: row.delegation_status || row.delegationStatus || "not_started",
  connectedAt: row.connected_at || row.connectedAt || "",
  active: row.active !== false,
  metadata: row.metadata && typeof row.metadata === "object" ? row.metadata : {},
});

export const normalizePointOfSaleRow = (row = {}) => ({
  id: row.id || "",
  issuerId: row.issuer_id || row.issuerId || "",
  branchId: row.branch_id || row.branchId || "",
  pointOfSale: Number(row.point_of_sale ?? row.pointOfSale ?? 0) || null,
  label: row.label || "",
  salesChannel: row.sales_channel || row.salesChannel || "all",
  active: row.active !== false,
  verifiedAt: row.verified_at || row.verifiedAt || "",
  metadata: row.metadata && typeof row.metadata === "object" ? row.metadata : {},
});

export const getRestaurantFiscalConnections = async (companyId) => {
  ensureSupabaseReady();

  if (!companyId) {
    throw new Error("No encontramos una empresa activa para cargar la conexion fiscal.");
  }

  const { data: issuerRows, error: issuerError } = await supabase
    .from("fiscal_issuers")
    .select(
      "id, company_id, environment, cuit, legal_name, iva_condition_id, iva_condition_label, gross_income_number, fiscal_address, delegation_status, connected_at, active, metadata"
    )
    .eq("issuer_kind", "restaurant")
    .eq("company_id", companyId)
    .order("environment", { ascending: false });

  if (issuerError) {
    throw normalizeFiscalConnectionError(
      issuerError,
      "No se pudo cargar la conexion fiscal del restaurante."
    );
  }

  const issuers = (issuerRows || []).map(normalizeIssuer);
  const issuerIds = issuers.map((issuer) => issuer.id).filter(Boolean);

  if (!issuerIds.length) {
    return [];
  }

  const { data: pointRows, error: pointError } = await supabase
    .from("fiscal_points_of_sale")
    .select(
      "id, issuer_id, branch_id, point_of_sale, label, sales_channel, active, verified_at, metadata"
    )
    .in("issuer_id", issuerIds)
    .eq("active", true)
    .order("point_of_sale", { ascending: true });

  if (pointError) {
    throw normalizeFiscalConnectionError(
      pointError,
      "No se pudieron cargar los puntos de venta fiscales."
    );
  }

  const points = (pointRows || []).map(normalizePointOfSaleRow);

  return issuers.map((issuer) => ({
    ...issuer,
    pointsOfSale: points.filter((point) => point.issuerId === issuer.id),
  }));
};

export const saveRestaurantFiscalIssuerDraft = async (companyId, payload = {}) => {
  ensureSupabaseReady();

  const validation = validateRestaurantFiscalDraft(payload) || validateFiscalContact(payload);
  if (validation) throw new Error(validation);

  const { data, error } = await supabase.rpc("save_restaurant_fiscal_onboarding_draft", {
    p_company_id: companyId,
    p_environment: normalizeFiscalEnvironment(payload.environment),
    p_cuit: normalizeCuit(payload.cuit),
    p_legal_name: `${payload.legalName || ""}`.trim(),
    p_iva_condition_id: Number(payload.ivaConditionId),
    p_iva_condition_label: `${payload.ivaConditionLabel || ""}`.trim(),
    p_gross_income_number: `${payload.grossIncomeNumber || ""}`.trim() || null,
    p_fiscal_address: `${payload.fiscalAddress || ""}`.trim(),
    p_contact: normalizeFiscalContact(payload),
  });

  if (error) {
    throw normalizeFiscalConnectionError(
      error,
      "No se pudieron guardar los datos fiscales del restaurante."
    );
  }

  return normalizeIssuer(data || {});
};

export const saveRestaurantFiscalPointOfSale = async (
  companyId,
  { environment, branchId, pointOfSale, label }
) => {
  ensureSupabaseReady();

  const { data, error } = await supabase.rpc("save_restaurant_fiscal_point_of_sale", {
    p_company_id: companyId,
    p_environment: normalizeFiscalEnvironment(environment),
    p_branch_id: branchId,
    p_point_of_sale: Number(pointOfSale),
    p_label: `${label || ""}`.trim() || null,
  });

  if (error) {
    throw normalizeFiscalConnectionError(
      error,
      "No se pudo guardar el punto de venta de la sucursal."
    );
  }

  return normalizePointOfSaleRow(data || {});
};

export const requestRestaurantFiscalConnectionVerification = async (
  companyId,
  environment
) => {
  ensureSupabaseReady();

  const { data, error } = await supabase.rpc(
    "request_restaurant_fiscal_connection_verification",
    {
      p_company_id: companyId,
      p_environment: normalizeFiscalEnvironment(environment),
    }
  );

  if (error) {
    throw normalizeFiscalConnectionError(
      error,
      "No se pudo enviar la conexion fiscal a revision."
    );
  }

  return normalizeIssuer(data || {});
};
